import React, { useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import cloneDeep from 'lodash.clonedeep';
import { ListDisplayTypes } from '../../constants';
import * as hooks from '../../hooks';
import { useEventCallback } from '../../ui/hooks';
import { useIsMobile } from '@/hooks/use-mobile';
import type { SearchOptions } from './types';

export const FILTERS_BAR_HEIGHT = 55;

/* hooks/useAsyncSearch.js and hooks/useSearchConfig.js are untyped JavaScript. */
type QueryField = { field: string; value: unknown; operator?: string };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: hooks/useSearchConfig.js setters take field objects or updaters
type QueryParams = [QueryField[] | Record<string, unknown>, (next?: any) => void, (next?: any) => unknown];
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: hooks/index.js
const js = hooks as any;
const useSortQueryParams = (): QueryParams => js.useSortQueryParams();
const useFilterQueryParams = (): QueryParams => js.useFilterQueryParams();
const useSearchQueryParams = (): QueryParams => js.useSearchQueryParams();

export type SearchQueries = { sort: QueryParams; filter: QueryParams; search: QueryParams };

const SearchQueriesContext = React.createContext<SearchQueries>({
  sort: [{}, () => {}, () => {}],
  filter: [[], () => {}, () => {}],
  search: [[], () => {}, () => {}],
});

export const useSearchQueriesContext = () => React.useContext(SearchQueriesContext);

export const ProvideSearchQueriesContext = ({ children }: { children: ReactNode }) => {
  const sort = useSortQueryParams();
  const filter = useFilterQueryParams();
  const search = useSearchQueryParams();
  const context = useMemo(() => ({ sort, filter, search }), [sort, filter, search]);
  return <SearchQueriesContext.Provider value={context}>{children}</SearchQueriesContext.Provider>;
};

export type SearchConfig = { filters?: unknown[]; search?: { field: string; value: string }[]; sort?: unknown };

export type SearchApi = {
  initialConfig: RefObject<SearchConfig | undefined>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: the rows are whatever getData returns
  data: any[];
  cached: boolean;
  requestingMore: boolean;
  requestMoreData: () => void;
  /** Whether another page may exist (useAsyncSearch's more-data state). */
  hasMore: boolean;
  /** The last next-page request failed and the rows already loaded were kept (`keepRowsOnFailedPage`). */
  failed: boolean;
  toggleDisplayType: () => void;
  displayType: string;
  loadingFromCache: boolean;
  loading: boolean;
  saveSettings: (next: SearchConfig) => void;
  appSettingsKey?: string;
  searchOptions: SearchOptions;
};

const SearchApiContext = React.createContext<SearchApi>({} as SearchApi);

export const useSearchApiContext = () => React.useContext(SearchApiContext);

type ScrollTarget = { scrollTo?: (to: { x: number; y: number; animated: boolean }) => void };

export type ProvideSearchAPIContextProps = {
  searchPath?: string;
  data?: unknown[];
  searchOptions: SearchOptions;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: a useApi method
  getData: (...args: any[]) => Promise<any>;
  appSettingsKey?: string;
  cacheKey?: string;
  listRef?: RefObject<ScrollTarget | null | undefined>;
  children: ReactNode;
  [option: string]: unknown;
};

/**
 * The data behind a searchable list: the current page(s) from getData under the URL's search,
 * filters and sort (useAsyncSearch), the stored search settings, and the cards/table choice.
 */
export const ProvideSearchAPIContext = (props: ProvideSearchAPIContextProps) => {
  const { searchPath, data: overrideValue = undefined, searchOptions, getData, appSettingsKey, cacheKey, listRef, children, ...rest } = props;

  const [{ settings, saveSettings: saveAppSettings }] = js.useAppState();
  const storedConfig = useRef<SearchConfig | undefined>(undefined);
  if (
    appSettingsKey &&
    !storedConfig.current &&
    settings &&
    settings.current &&
    settings.current[appSettingsKey] &&
    settings.current[appSettingsKey].searchConfig
  ) {
    storedConfig.current = cloneDeep(settings.current[appSettingsKey].searchConfig);
  }

  const saveSettings = useEventCallback((nextSetting: SearchConfig) => {
    if (nextSetting && appSettingsKey && typeof appSettingsKey === 'string') {
      saveAppSettings({
        [appSettingsKey]: {
          searchConfig: nextSetting,
        },
      });
    }
  });

  const {
    value: data,
    cached,
    status,
    requestingMore,
    requestMoreData,
    moreDataAvailable,
    moreFailed,
  } = js.useAsyncSearch(getData, {
    options: searchOptions,
    initialConfig: storedConfig.current,
    limit: 30,
    offset: 0,
    overrideValue,
    cache: true,
    cacheKey,
    searchPath,
    onChange: (_apiConfig: unknown, searchConfig: SearchConfig | null) => {
      if (listRef && listRef.current && listRef.current.scrollTo) {
        listRef.current.scrollTo({ x: 0, y: 0, animated: false });
      }
      if (searchConfig) {
        const { filters = [], search = [], sort = {} } = searchConfig;
        saveSettings({ filters, sort, search });
      }
    },
    ...rest,
  });

  // A phone always gets cards (today's xs rule, now shadcn's 768px mobile width).
  const isMobile = useIsMobile();
  const [actualDisplayType, setActualDisplayType] = useState<string>(
    settings.current && settings.current.listDisplayType ? settings.current.listDisplayType : ListDisplayTypes.CARDS
  );
  const displayType = isMobile ? ListDisplayTypes.CARDS : actualDisplayType;
  const loadingFromCache = !requestingMore && status === 'pending' && cached && data;
  const loading = !requestingMore && status === 'pending' && !cached;
  const failed = !!moreFailed;

  const toggleDisplayType = useEventCallback(() => {
    const nextListDisplayType = actualDisplayType === ListDisplayTypes.CARDS ? ListDisplayTypes.TABLE : ListDisplayTypes.CARDS;

    saveAppSettings({
      listDisplayType: nextListDisplayType,
    });
    setActualDisplayType(nextListDisplayType);
  });

  const context = useMemo(() => {
    const value = {
      initialConfig: storedConfig,
      data,
      cached,
      requestingMore,
      requestMoreData,
      hasMore: !!moreDataAvailable,
      failed,
      toggleDisplayType,
      displayType,
      loadingFromCache,
      loading,
      saveSettings,
      appSettingsKey,
      searchOptions,
    };
    console.log(`SearchApiContext for ${appSettingsKey}:`, value);
    return value;
  }, [
    data,
    cached,
    requestingMore,
    requestMoreData,
    moreDataAvailable,
    failed,
    toggleDisplayType,
    displayType,
    loadingFromCache,
    loading,
    saveSettings,
    appSettingsKey,
    searchOptions,
  ]);

  return (
    <SearchApiContext.Provider value={context as SearchApi}>
      <ProvideSearchQueriesContext>{children}</ProvideSearchQueriesContext>
    </SearchApiContext.Provider>
  );
};
